/**
 * Platform Console — /admin/platform
 *
 * Restricted grouping of technical / infrastructure administration tools.
 * Access limited to super_admin. Non-super admins see the "restricted"
 * screen. Non-admins are bounced to /admin/login by the axios interceptor.
 *
 * Phase 7C intentionally does NOT rebuild the underlying tools — it
 * groups the existing legacy admin routes under one heading and clearly
 * marks technical / restricted / planned status per card.
 */
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { adminAuth } from "@/lib/api";
import { Wrench, Webhook, Activity, Database, ArrowLeft, ShieldAlert } from "lucide-react";

const TOOLS = [
  { key: "kcis-make",      title: "KCIS Make Operations", to: "/admin/kcis-make-operations", desc: "Integrations, webhooks, and Make.com automation ops.", Icon: Webhook, status: "Active", restricted: true },
  { key: "adaptive-sessions", title: "KCIS Adaptive Sessions", to: "/admin/kcis-adaptive-sessions", desc: "Adaptive-engine session inspection and diagnostics.", Icon: Activity, status: "Active", restricted: true },
  { key: "adaptive-reporting", title: "KCIS Adaptive Reporting", to: "/admin/kcis-adaptive-reporting", desc: "Adaptive session reporting.", Icon: Activity, status: "Active", restricted: true },
  { key: "webhooks-monitor", title: "Webhooks Monitor", to: "/admin/platform", desc: "Central webhooks + dead-letter dashboard.", Icon: Webhook, status: "Planned", restricted: true },
  { key: "queues",         title: "Background Queues", to: "/admin/platform", desc: "Job queues, retry state, and worker health.", Icon: Database, status: "Planned", restricted: true },
  { key: "diagnostics",    title: "Diagnostics", to: "/admin/platform", desc: "Runtime health, error rates, deploy history.", Icon: Activity, status: "Planned", restricted: true },
];

export default function PlatformConsole() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [role, setRole] = useState("");

  useEffect(() => {
    const token = adminAuth.getToken();
    if (!token) {
      navigate(`/admin/login?next=${encodeURIComponent("/admin/platform")}`, { replace: true });
      return;
    }
    setRole(adminAuth.getRole());
    setReady(true);
  }, [navigate]);

  if (!ready) return null;
  const isSuper = role === "super_admin" || adminAuth.isSuperAdmin();

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="platform-console">
      <header className="border-b border-neutral-800 bg-neutral-900">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            <span className="font-semibold">KCIS Platform Console</span>
            <span className="text-xs text-neutral-500 ml-2">Restricted · Technical Administration</span>
          </div>
          <Link to="/admin" className="text-xs text-neutral-300 hover:underline flex items-center gap-1" data-testid="platform-back-portal">
            <ArrowLeft className="w-3 h-3" /> Enterprise Portal
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {!isSuper && (
          <div className="rounded-2xl border border-amber-800 bg-amber-950/40 p-5 mb-8 flex gap-3" data-testid="platform-restricted">
            <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-amber-200">Restricted area</div>
              <p className="mt-1 text-xs text-neutral-300">
                The Platform Console is limited to super administrators. You can view the tool inventory below, but individual tools may deny access. Contact the platform owner if you need elevated permissions.
              </p>
            </div>
          </div>
        )}

        <div className="text-xs uppercase tracking-widest text-neutral-500">Technical Administration</div>
        <h1 className="mt-1 text-3xl font-semibold">Platform Console</h1>
        <p className="mt-2 max-w-2xl text-sm text-neutral-400">
          Integrations, webhooks, queues, and diagnostics. This console is separate from the KCIS Enterprise Portal and University Operations Workspace. Restricted to technical administrators.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          {TOOLS.map((t) => (
            <Link key={t.key} to={t.to}
                  className="rounded-2xl border border-neutral-800 bg-neutral-900/60 hover:bg-neutral-900 p-5 transition-colors block"
                  data-testid={`platform-tool-${t.key}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 flex items-center justify-center">
                  <t.Icon className="w-4 h-4 text-neutral-300" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{t.title}</div>
                  <div className="text-xs text-neutral-500">{t.status}</div>
                </div>
              </div>
              <p className="mt-3 text-xs text-neutral-400 leading-relaxed">{t.desc}</p>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
